/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * SafeButtonWrapper - Prevents multiple clicks on any wrapped button
 *
 * @file SafeButtonWrapper.jsx
 * @author Satyaki Bose
 * @since 1.0.0
 *
 * Usage (self-managed):
 *   <SafeButtonWrapper>
 *     <Button onClick={handleSubmit}>Submit</Button>
 *   </SafeButtonWrapper>
 *
 * Usage (parent-controlled):
 *   <SafeButtonWrapper isLoading={apiLoading}>
 *     <Button onClick={handleSubmit}>Submit</Button>
 *   </SafeButtonWrapper>
 *
 * Usage (with spinner):
 *   <SafeButtonWrapper isLoading={apiLoading} showSpinner spinnerPosition="end">
 *     <Button onClick={handleSubmit}>Submit</Button>
 *   </SafeButtonWrapper>
 */

import React, { useState, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';

const SafeButtonWrapper = ({
  children,
  isLoading = null,
  resetDelay,
  onClickCapture,
  showSpinner = true,
  spinnerSize = 16,
  spinnerPosition = 'end'
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Use parent's isLoading if provided, otherwise use internal state
  const isDisabledByWrapper = isLoading != null ? isLoading : isProcessing;

  const handleClick = (originalOnClick) => (e) => {
    if (isDisabledByWrapper) {
      e.preventDefault();
      e.stopPropagation();

      return;
    }

    if (isLoading == null) {
      setIsProcessing(true);
      setTimeout(() => setIsProcessing(false), resetDelay);
    }

    if (onClickCapture) {
      onClickCapture(e);
    }

    if (originalOnClick) {
      originalOnClick(e);
    }
  };

  // Clone the child and override onClick + disabled
  const child = React.Children.only(children);
  const isDisabled = child.props.disabled || isDisabledByWrapper;

  // Build enhanced children with spinner when loading
  const enhancedChildren = isDisabledByWrapper && showSpinner ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {spinnerPosition === 'start' && (
        <CircularProgress size={spinnerSize} color="inherit" />
      )}
      {child.props.children}
      {spinnerPosition === 'end' && (
        <CircularProgress size={spinnerSize} color="inherit" />
      )}
    </span>
  ) : child.props.children;

  const clonedChild = cloneElement(child, {
    onClick: handleClick(child.props.onClick),
    disabled: isDisabled,
    children: enhancedChildren
  });

  if (isDisabledByWrapper) {
    return (
      <span
        style={{
          display: 'inline-block',
          cursor: 'not-allowed'
        }}
      >
        {clonedChild}
      </span>
    );
  }

  return clonedChild;
};

SafeButtonWrapper.propTypes = {
  children: PropTypes.element.isRequired,
  isLoading: PropTypes.bool,
  resetDelay: PropTypes.number,
  onClickCapture: PropTypes.func,
  showSpinner: PropTypes.bool,
  spinnerSize: PropTypes.number,
  spinnerPosition: PropTypes.oneOf(['start', 'end'])
};

SafeButtonWrapper.defaultProps = {
  isLoading: null,
  resetDelay: 5000,
  onClickCapture: null,
  showSpinner: true,
  spinnerSize: 16,
  spinnerPosition: 'end'
};

export default SafeButtonWrapper;
